﻿// src/components/GeoMap.jsx
import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { MapContainer, TileLayer } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "@geoman-io/leaflet-geoman-free";
import "@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css";

import { useAuth } from "@/context/auth.js";

/**
 * GeoMap (API-first / multi-tenant safe):
 * - Renderiza SOLO lo que llega por props.geocercas.
 * - NO persiste geocercas en localStorage (nunca).
 * - Limpia caches legacy "geocerca_*" que venían de versiones antiguas.
 * - Dibuja (según `geocercas`) y emite eventos:
 *    - onCreateFeature({ orgId, nombre, color, geojson, polygon, layer })
 *    - onEditFeature({ orgId, id, geojson, polygon, nombre, color, layer })
 *    - onDeleteFeature({ orgId, id, layer })
 */

// --- safeParseJSON "blindado" (se conserva) ---
function safeParseJSON(input, label = "JSON") {
  if (!input) return null;

  if (typeof input === "object") return input;

  if (typeof input !== "string") {
    console.warn(`[GeoMap] ${label} tipo inesperado:`, typeof input);
    return null;
  }

  try {
    return JSON.parse(input);
  } catch (e) {
    const featureIdx = input.indexOf('{"type"');
    if (featureIdx >= 0) {
      const candidate = input.slice(featureIdx);
      try {
        return JSON.parse(candidate);
      } catch (_) {}
    }

    const firstBracket = input.indexOf("[");
    const lastBracket = input.lastIndexOf("]");
    if (firstBracket >= 0 && lastBracket > firstBracket) {
      const arrCandidate = input.slice(firstBracket, lastBracket + 1);
      try {
        return JSON.parse(arrCandidate);
      } catch (_) {}
    }

    console.warn(`[GeoMap] ${label} inválido (no se pudo parsear):`, input);
    return null;
  }
}

function latLngsFromGeomField(geomInput) {
  const geom = safeParseJSON(geomInput, "geom/geojson");
  if (!geom) return null;

  if (geom.type === "FeatureCollection" && Array.isArray(geom.features)) {
    if (!geom.features.length) return null;
    return latLngsFromGeomField(geom.features[0]);
  }

  if (geom.type === "Feature" && geom.geometry) {
    return latLngsFromGeomField(geom.geometry);
  }

  const g = geom;
  if (!g || !g.type || !g.coordinates) return null;

  let ring = null;

  if (g.type === "Polygon" && Array.isArray(g.coordinates[0])) {
    ring = g.coordinates[0]; // [[lng,lat], ...]
  } else if (g.type === "MultiPolygon" && Array.isArray(g.coordinates[0])) {
    ring = g.coordinates[0][0]; // primer polígono, primer anillo
  } else {
    console.warn("[GeoMap] geom no es Polygon/MultiPolygon:", g.type);
    return null;
  }

  const out = ring
    .filter(
      (pt) =>
        Array.isArray(pt) &&
        typeof pt[0] === "number" &&
        typeof pt[1] === "number"
    )
    .map(([lng, lat]) => [lat, lng]);

  return out.length >= 3 ? out : null;
}

function latLngsFromPolygonField(rawPolygon) {
  if (!rawPolygon) return null;

  const poly = safeParseJSON(rawPolygon, "polygon");
  if (!poly || !Array.isArray(poly) || poly.length < 3) return null;

  if (poly[0] && poly[0].lat != null && poly[0].lng != null) {
    const arr = poly
      .filter((p) => p.lat != null && p.lng != null)
      .map((p) => [p.lat, p.lng]);
    return arr.length >= 3 ? arr : null;
  }

  if (
    Array.isArray(poly[0]) &&
    typeof poly[0][0] === "number" &&
    typeof poly[0][1] === "number"
  ) {
    return poly.length >= 3 ? poly : null;
  }

  console.warn("[GeoMap] formato polygon no soportado:", poly);
  return null;
}

function getLatLngsFromRow(row) {
  const geomField = row.geojson ?? row.geom ?? row.geometry;
  const fromGeom = latLngsFromGeomField(geomField);
  if (fromGeom && fromGeom.length >= 3) {
    return { type: "polygon", latlngs: fromGeom, source: "geojson/geom" };
  }

  const fromPolygon = latLngsFromPolygonField(row.polygon);
  if (fromPolygon && fromPolygon.length >= 3) {
    return { type: "polygon", latlngs: fromPolygon, source: "polygon" };
  }

  if (row.lat != null && row.lng != null) {
    const radius = row.radius_m || 50;
    return {
      type: "circle",
      center: [row.lat, row.lng],
      radius,
      source: "circle",
    };
  }

  return null;
}

function drawGeocercaOnGroup(fg, row, canEdit) {
  const id = row.id;
  const nombre = row.nombre || row.name || "Geocerca";
  const color = row.color || "#2563eb";

  const shape = getLatLngsFromRow(row);
  if (!shape) {
    console.warn("[GeoMap] geocerca sin geometría usable, id:", id, row);
    return false;
  }

  let layer = null;

  if (shape.type === "polygon") {
    layer = L.polygon(shape.latlngs, { color });
  } else if (shape.type === "circle") {
    layer = L.circle(shape.center, { radius: shape.radius, color });
  }

  if (!layer) return false;

  layer._dbId = id;
  layer.bindTooltip(nombre, { sticky: true });

  if (!canEdit && layer.pm) layer.pm.disable();
  layer.addTo(fg);

  return true;
}

// Convierte Feature Polygon a polygon legacy [{lat,lng}, ...] (compat)
function polygonLegacyFromFeature(gj) {
  try {
    if (
      gj?.geometry?.type === "Polygon" &&
      Array.isArray(gj.geometry.coordinates?.[0])
    ) {
      return gj.geometry.coordinates[0]
        .filter(
          (pt) =>
            Array.isArray(pt) &&
            typeof pt[0] === "number" &&
            typeof pt[1] === "number"
        )
        .map(([lng, lat]) => ({ lat, lng }));
    }
  } catch {}
  return null;
}

function cleanupLegacyGeocercaCache() {
  try {
    if (typeof window === "undefined") return;
    const keys = Object.keys(window.localStorage || {});
    for (const k of keys) {
      // OK legacy: geocerca_<Nombre>
      if (String(k).toLowerCase().startsWith("geocerca_")) {
        window.localStorage.removeItem(k);
      }
    }
  } catch (e) {
    console.warn("[GeoMap] cleanupLegacyGeocercaCache failed:", e);
  }
}

export default function GeoMap({
  canEdit = false,
  orgId: orgIdProp,
  geocercas = [],
  getNewFeatureMeta,
  onCreateFeature,
  onEditFeature,
  onDeleteFeature,
}) {
  const { t } = useTranslation();
  const { currentOrg } = useAuth() || {};
  const orgId = orgIdProp ?? currentOrg?.id ?? null;

  const mapRef = useRef(null);
  const groupRef = useRef(null);
  const controlsAddedRef = useRef(false);

  const center = useMemo(() => [-1.8312, -78.1834], []); // Ecuador
  const zoom = 6;

  // OK Limpia cache legacy UNA vez al montar
  useEffect(() => {
    cleanupLegacyGeocercaCache();
  }, []);

  // Debug útil
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.__debug_orgId = orgId || null;
      window.__debug_canEdit = !!canEdit;
      console.log("[GeoMap] orgId/canEdit:", orgId, canEdit);
    }
  }, [orgId, canEdit]);

  // Redibujar cuando cambian las geocercas
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    let fg = groupRef.current;
    if (!fg) {
      fg = L.featureGroup().addTo(map);
      groupRef.current = fg;
    }

    // Controles Geoman una sola vez
    if (!controlsAddedRef.current) {
      map.pm.addControls({
        position: "topleft",
        drawMarker: false,
        drawCircleMarker: false,
        drawCircle: true,
        drawPolyline: false,
        drawRectangle: true,
        drawPolygon: true,
        cutPolygon: false,
        editMode: false,
        dragMode: false,
        removalMode: false,
      });
      controlsAddedRef.current = true;
    }

    // Habilitar / deshabilitar modos globales según permisos
    try {
      map.pm.disableGlobalEditMode?.();
      map.pm.disableGlobalDragMode?.();
      map.pm.disableGlobalRemovalMode?.();
      map.pm.disableDraw?.();
    } catch {}

    // OK SOLO lo que viene por props.geocercas
    fg.clearLayers();

    const safeRows = Array.isArray(geocercas) ? geocercas : [];
    let anyLayer = false;

    safeRows.forEach((row) => {
      const ok = drawGeocercaOnGroup(fg, row, canEdit);
      if (ok) anyLayer = true;
    });

    if (anyLayer) {
      try {
        const bounds = fg.getBounds();
        if (bounds.isValid()) map.fitBounds(bounds.pad(0.15));
      } catch (e) {
        console.warn("[GeoMap] error en fitBounds:", e);
      }
    }
  }, [geocercas, canEdit, orgId]);

  // Eventos Geoman: SOLO emiten callbacks
  useEffect(() => {
    const map = mapRef.current;
    const fg = groupRef.current;
    if (!map || !fg) return;

    const requireWritableContext = () => {
      if (!canEdit) return { ok: false, msg: t("geocercas.errors.noEditPermission") };
      if (!orgId) return { ok: false, msg: t("geocercas.errors.selectOrganizationFirst") };
      return { ok: true };
    };

    const onCreate = async (e) => {
      const gate = requireWritableContext();
      if (!gate.ok) {
        // Solo mensaje claro de acción, sin detalles técnicos
        alert(gate.msg);
        e.layer?.remove?.();
        return;
      }

      const layer = e.layer;

      // Manejo especial para círculos creados con Geoman
      if (e.shape === "Circle" && layer?.getLatLng && layer?.getRadius) {
        const meta = getNewFeatureMeta?.() || {};
        const color = meta.color || "#2563eb";
        const nombreMeta = meta.nombre?.trim();

        const nombre =
          nombreMeta || window.prompt("Nombre de la geocerca:") || "Geocerca";

        const initialRadius = Math.round(layer.getRadius() || 0);
        const suggestedRadius = initialRadius > 5 ? initialRadius : 100;

        const radiusInput = window.prompt(
          "Radio del círculo en metros:",
          String(suggestedRadius)
        );

        if (radiusInput === null) {
          layer?.remove?.();
          return;
        }

        const radiusMeters = Number(String(radiusInput).replace(",", "."));

        if (!Number.isFinite(radiusMeters) || radiusMeters <= 0) {
          alert("Radio inválido. Ingresa un número mayor a 0.");
          layer?.remove?.();
          return;
        }

        layer.setRadius(radiusMeters);

        const center = layer.getLatLng();

        if (layer.setStyle) layer.setStyle({ color });

        layer.bindTooltip(`${nombre} · radio ${Math.round(radiusMeters)} m`, {
          sticky: true,
        });

        console.log("[GeoMap] circle_created", {
          lat: center.lat,
          lng: center.lng,
          radiusMeters,
        });

        try {
          if (typeof onCreateFeature === "function") {
            const result = await onCreateFeature({
              orgId,
              nombre,
              color,
              type: "circle",
              center: { lat: center.lat, lng: center.lng },
              radius_m: radiusMeters,
              geojson: layer.toGeoJSON(),
              layer,
            });

            if (result?.id) layer._dbId = result.id;
          } else {
            layer._draft = true;
          }

          fg.addLayer(layer);
        } catch (err) {
          alert("No se pudo guardar la geocerca.");
          layer?.remove?.();
        }

        return;
      }

      const gj = layer.toGeoJSON(); // Feature completo
      const polygon = polygonLegacyFromFeature(gj);

      const meta = (getNewFeatureMeta?.() || {});
      const color = meta.color || "#2563eb";
      const nombreMeta = meta.nombre?.trim();

      const nombre =
        nombreMeta || window.prompt("Nombre de la geocerca:") || "Geocerca";

      if (layer.setStyle) layer.setStyle({ color });
      layer.bindTooltip(`${nombre}`, { sticky: true });

      try {
        if (typeof onCreateFeature === "function") {
          const result = await onCreateFeature({
            orgId,
            nombre,
            color,
            geojson: gj,
            polygon,
            layer,
          });
          if (result?.id) layer._dbId = result.id;
        } else {
          layer._draft = true;
        }

        fg.addLayer(layer);
      } catch (err) {
        // Error de guardado, mensaje simple
        alert("No se pudo guardar la geocerca.");
        layer?.remove?.();
      }
    };

    const onEdit = async (e) => {
      const gate = requireWritableContext();
      if (!gate.ok) return;

      const layers = e.layers || new L.LayerGroup([e.layer]);
      layers.eachLayer(async (layer) => {
        try {
          const id = layer._dbId;
          if (!id) return;

          const gj = layer.toGeoJSON();
          const polygon = polygonLegacyFromFeature(gj);

          const tooltip = layer.getTooltip?.();
          const nombre = tooltip?.getContent?.() || undefined;
          const stroke = layer.options?.color || undefined;

          if (typeof onEditFeature === "function") {
            await onEditFeature({
              orgId,
              id,
              geojson: gj,
              polygon,
              nombre,
              color: stroke,
              layer,
            });
          }
        } catch (err) {
          // Error de actualización, mensaje simple
          alert("No se pudo actualizar la geocerca.");
        }
      });
    };

    const onRemove = async (e) => {
      const gate = requireWritableContext();
      if (!gate.ok) return;

      const layers = e.layers || new L.LayerGroup([e.layer]);
      layers.eachLayer(async (layer) => {
        try {
          const id = layer._dbId;
          if (!id) return;

          if (typeof onDeleteFeature === "function") {
            await onDeleteFeature({ orgId, id, layer });
          }
        } catch (err) {
          // Error de borrado, mensaje simple
          alert("No se pudo eliminar la geocerca.");
        }
      });
    };

    // OK register events
    map.on("pm:create", onCreate);
    map.on("pm:edit", onEdit);
    map.on("pm:remove", onRemove);

    return () => {
      map.off("pm:create", onCreate);
      map.off("pm:edit", onEdit);
      map.off("pm:remove", onRemove);
    };
  }, [canEdit, orgId, getNewFeatureMeta, onCreateFeature, onEditFeature, onDeleteFeature]);

  return (
    <div className="w-full">
      <MapContainer
        center={center}
        zoom={zoom}
        style={{ height: "70vh", width: "100%" }}
        whenCreated={(map) => {
          mapRef.current = map;
        }}
      >
        <TileLayer
          attribution='&copy; OpenStreetMap contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
      </MapContainer>
    </div>
  );
}
